<template>
  <div class="arc-settings">
    <div v-if="!ready" class="arc-empty">
      {{ $t('ARC_MGMT.LOADING') }}
    </div>

    <div v-else-if="!isConsultant" class="arc-empty">
      <h3>{{ $t('ARC_MGMT.RESTRICTED.TITLE') }}</h3>
      <p>{{ $t('ARC_MGMT.RESTRICTED.BODY') }}</p>
    </div>

    <div v-else-if="!journey.id" class="arc-empty">
      <h3>{{ $t('ARC_MGMT.NO_JOURNEY.TITLE') }}</h3>
      <p>{{ $t('ARC_MGMT.NO_JOURNEY.BODY') }}</p>
    </div>

    <template v-else>
      <header class="arc-head">
        <div>
          <p class="arc-sub">
            <b>{{ picLabel }}</b> ·
            <span class="arc-pill">{{ $t('ARC_MGMT.MONTH_OF', { n: journey.mes_atual }) }} · {{ journey.fase && journey.fase.nome }}</span>
          </p>
        </div>
        <div v-if="cycles.length > 1" class="arc-cycle">
          <label>{{ $t('ARC_MGMT.CYCLE') }}</label>
          <select v-model="selectedCycle" @change="onCycleChange">
            <option v-for="c in cycles" :key="c.journey_id" :value="c.ciclo">
              {{ $t('ARC_MGMT.CYCLE') }} {{ c.ciclo }}
            </option>
          </select>
        </div>
      </header>

      <nav class="arc-tabs">
        <button
          v-for="t in tabs"
          :key="t.key"
          class="arc-tab"
          :class="{ active: activeTab === t.key }"
          type="button"
          @click="activeTab = t.key"
        >
          {{ $t('ARC_MGMT.TABS.' + t.key) }}
        </button>
      </nav>

      <section class="arc-body">
        <component :is="activeComponent" :journey="journey" :cycle="selectedCycle" @refresh="loadJourney" />
      </section>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ArcAPI from 'dashboard/api/arc';
import ArcGeneral from './sections/General.vue';
import ArcDeliverables from './sections/Deliverables.vue';
import ArcIndicators from './sections/Indicators.vue';
import ArcValue from './sections/Value.vue';
import ArcMeetings from './sections/Meetings.vue';
import ArcDecisions from './sections/Decisions.vue';
import ArcPendings from './sections/Pendings.vue';
import ArcBadges from './sections/Badges.vue';

export default {
  components: {
    ArcGeneral, ArcDeliverables, ArcIndicators, ArcValue,
    ArcMeetings, ArcDecisions, ArcPendings, ArcBadges,
  },
  data() {
    return {
      ready: false,
      activeTab: 'GENERAL',
      cycles: [],
      selectedCycle: null,
      tabs: [
        { key: 'GENERAL', comp: 'ArcGeneral' },
        { key: 'DELIVERABLES', comp: 'ArcDeliverables' },
        { key: 'INDICATORS', comp: 'ArcIndicators' },
        { key: 'VALUE', comp: 'ArcValue' },
        { key: 'MEETINGS', comp: 'ArcMeetings' },
        { key: 'DECISIONS', comp: 'ArcDecisions' },
        { key: 'PENDINGS', comp: 'ArcPendings' },
        { key: 'BADGES', comp: 'ArcBadges' },
      ],
      picLabels: {
        protecao_veicular: 'Proteção Veicular', laboratorio: 'Laboratório',
        clinica: 'Clínica Média', ecommerce: 'E-commerce', imobiliaria: 'Imobiliária',
      },
    };
  },
  computed: {
    ...mapGetters({
      journey: 'arc/getJourney',
      isConsultant: 'arc/getIsConsultant',
      access: 'arc/getAccess',
    }),
    activeComponent() {
      return (this.tabs.find(t => t.key === this.activeTab) || this.tabs[0]).comp;
    },
    picLabel() {
      return this.picLabels[this.journey.pic] || this.journey.pic;
    },
  },
  async mounted() {
    await this.$store.dispatch('arc/fetchAccess');
    if (this.isConsultant) {
      await this.loadJourney();
      await this.loadCycles();
    }
    this.ready = true;
  },
  methods: {
    async loadJourney() {
      await this.$store.dispatch('arc/fetchJourney');
      this.selectedCycle = this.selectedCycle || this.journey.ciclo;
    },
    async loadCycles() {
      try {
        const { data } = await ArcAPI.cycles();
        this.cycles = data.payload || [];
      } catch (e) {
        this.cycles = [];
      }
    },
    onCycleChange() {
      // Recarrega dados do ciclo selecionado (as seções reagem via prop cycle).
      this.$emit('refresh');
    },
  },
};
</script>

<style lang="scss" scoped>
.arc-settings {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  padding: var(--space-normal) var(--space-large);
  font-family: Inter, sans-serif;
}
.arc-empty {
  padding: var(--space-larger);
  text-align: center;
  color: var(--s-500);
  h3 { color: var(--s-800); margin-bottom: var(--space-small); }
}
.arc-head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: var(--space-normal);
  h2 { font-size: var(--font-size-medium); font-weight: var(--font-weight-bold); color: var(--s-900); margin: 0; }
}
.arc-sub { color: var(--s-500); font-size: var(--font-size-small); margin-top: var(--space-smaller); }
.arc-pill {
  background: var(--w-50); color: var(--w-700); border-radius: 9999px;
  padding: var(--space-micro) var(--space-small); font-weight: var(--font-weight-medium); font-size: var(--font-size-mini);
}
.arc-cycle { display: flex; align-items: center; gap: var(--space-small);
  label { font-size: var(--font-size-mini); color: var(--s-500); }
  select { height: var(--space-large); border: 1px solid var(--s-200); border-radius: var(--border-radius-normal); padding: 0 var(--space-small); background: var(--white); color: var(--s-800); }
}
.arc-tabs {
  display: flex; gap: var(--space-smaller); border-bottom: 1px solid var(--s-100);
  margin: var(--space-normal) 0; overflow-x: auto; overflow-y: hidden; scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}
.arc-tab {
  border: 0; background: none; padding: var(--space-small) var(--space-normal);
  font-weight: var(--font-weight-medium); font-size: var(--font-size-small); color: var(--s-500);
  border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; cursor: pointer;
  &.active { color: var(--w-600); border-bottom-color: var(--w-500); }
  &:hover { color: var(--s-800); }
}
.arc-body { padding-top: var(--space-small); }
</style>
